/**
 * LabelingTab Component
 *
 * Dataset labeling interface that allows users to:
 * 1. Select a dataset from the available datasets
 * 2. View and label samples with OK/NOK classifications
 * 3. See labeling statistics
 */

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Tag, Database, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import { getDatasets } from "../api/datasets";
import { DatasetLabelingView } from "./DatasetLabelingView";
import type { Dataset } from "../types";
import type { DatasetResponse } from "../types/dataset";

interface LabelingTabProps {
  datasets: Dataset[];
  showMockData: boolean;
  /** Optional: Pre-select a dataset when navigating from Datasets tab */
  initialDatasetId?: string | null;
  /** Optional: Callback when dataset selection is cleared */
  onDatasetCleared?: () => void;
}

export function LabelingTab({
  datasets,
  showMockData,
  initialDatasetId,
  onDatasetCleared,
}: LabelingTabProps) {
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    initialDatasetId || null
  );

  // Sync with initialDatasetId when it changes (e.g., navigation from Datasets tab)
  useEffect(() => {
    if (initialDatasetId) {
      setSelectedDatasetId(initialDatasetId);
    }
  }, [initialDatasetId]);

  // Fetch datasets from API when not showing mock data
  const {
    data: apiDatasets,
    isLoading,
    isError,
  } = useQuery<DatasetResponse[]>({
    queryKey: ["ai", "datasets"],
    queryFn: getDatasets,
    enabled: !showMockData,
  });

  // Get display name from connector (e.g., "@InfluxDB.bucket" -> "bucket")
  const getStationFromConnector = (connectorName: string) => {
    return connectorName.replace("@InfluxDB.", "");
  };

  // Get datasets to display in selector
  const getAvailableDatasets = () => {
    if (showMockData) {
      return datasets;
    }
    return apiDatasets || [];
  };

  const availableDatasets = getAvailableDatasets();

  // Handle dataset selection
  const handleDatasetSelect = (datasetId: string) => {
    setSelectedDatasetId(datasetId);
  };

  // Handle back from labeling view
  const handleBackFromLabeling = () => {
    setSelectedDatasetId(null);
    onDatasetCleared?.();
  };

  // If a dataset is selected, show the labeling view
  if (selectedDatasetId) {
    return (
      <DatasetLabelingView
        datasetId={selectedDatasetId}
        onBack={handleBackFromLabeling}
      />
    );
  }

  // Otherwise show the dataset selector
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Dataset Labeling Interface
          </CardTitle>
          <CardDescription>Label production data samples for model training</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg">
            {isLoading ? (
              <>
                <Loader2 className="h-12 w-12 text-muted-foreground mb-4 animate-spin" />
                <h3 className="text-lg font-semibold mb-2">Loading Datasets...</h3>
                <Skeleton className="h-10 w-64" />
              </>
            ) : isError ? (
              <>
                <Database className="h-12 w-12 text-destructive/50 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to Load Datasets</h3>
                <p className="text-muted-foreground mb-4 max-w-md">
                  Unable to connect to the AI backend. Please check your connection and try again.
                </p>
              </>
            ) : availableDatasets.length === 0 ? (
              <>
                <Database className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Datasets Available</h3>
                <p className="text-muted-foreground mb-4 max-w-md">
                  Create a dataset in the Datasets tab first, then return here to start labeling.
                </p>
              </>
            ) : (
              <>
                <Tag className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Select a Dataset</h3>
                <p className="text-muted-foreground mb-4 max-w-md">
                  Choose a dataset to begin labeling. Classify samples as OK/NOK and add notes for specific observations.
                </p>
                <Select onValueChange={handleDatasetSelect}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select dataset to label" />
                  </SelectTrigger>
                  <SelectContent>
                    {showMockData ? (
                      datasets.map((dataset) => (
                        <SelectItem key={dataset.id} value={dataset.id}>
                          {dataset.name}
                        </SelectItem>
                      ))
                    ) : (
                      (apiDatasets || []).map((dataset) => (
                        <SelectItem key={dataset.id} value={dataset.id}>
                          <div className="flex items-center gap-2">
                            <span>{dataset.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({getStationFromConnector(dataset.connectorName)})
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Labeling Stats - Show aggregated stats when no dataset is selected */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              {isLoading ? (
                <>
                  <Skeleton className="h-10 w-24 mx-auto mb-1" />
                  <Skeleton className="h-4 w-32 mx-auto" />
                </>
              ) : (
                <>
                  <p className="text-4xl font-bold text-primary">
                    {showMockData ? "57,500" : (apiDatasets?.length || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {showMockData ? "Total Labeled Samples" : "Available Datasets"}
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              {isLoading ? (
                <>
                  <Skeleton className="h-10 w-24 mx-auto mb-1" />
                  <Skeleton className="h-4 w-32 mx-auto" />
                </>
              ) : (
                <>
                  <p className="text-4xl font-bold text-success">
                    {showMockData ? "94.2%" : "—"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">OK Samples</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              {isLoading ? (
                <>
                  <Skeleton className="h-10 w-24 mx-auto mb-1" />
                  <Skeleton className="h-4 w-32 mx-auto" />
                </>
              ) : (
                <>
                  <p className="text-4xl font-bold text-warning">
                    {showMockData ? "5.8%" : "—"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">NOK Samples</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
